import { SearchRounded } from "@mui/icons-material";
import {
    CircularProgress,
    IconButton,
    InputAdornment,
    TextField,
} from "@mui/material";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

const TextInputSearch = ({
  onSearch,
  onCancelSearch,
  min = 3,
  isSearching,
  label,
  onChange,
  initialValue,
  value,
}) => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState(String(initialValue || ""));
  const [searchHelpText, setSearchHelpText] = useState("");
  const initialized = useRef(null);
  const [touched, setTouched] = useState(false);
  const debounceTimeout = useRef(null);
  const previousSearchText = useRef(String(initialValue || ""));
  const resolvedValue =
    value !== undefined ? String(value || "") : String(initialValue || "");

  const handleChange = (e) => {
    if (e.target.value.length > 0 && !touched) {
      setTouched(true);
    }
    setSearchText(e.target.value);
    if (!onChange) return;
    if (debounceTimeout.current) {
      clearTimeout(debounceTimeout.current);
    }
    debounceTimeout.current = setTimeout(() => {
      onChange(e.target.value);
    }, 500);
  };

  const handleSearch = () => {
    if (searchText.length >= min) {
      onSearch(searchText);
      setSearchHelpText("");
    } else {
      setSearchHelpText(t(`por favor introduzca al menos ${min} caracteres`));
    }
  };

  useEffect(() => {
    const wasNonEmpty = Boolean(previousSearchText.current);
    const isNowEmpty = !searchText || searchText.length === 0;

    if (touched && wasNonEmpty && isNowEmpty) {
      if (typeof onCancelSearch === "function") {
        onCancelSearch();
      } else if (typeof onSearch === "function") {
        onSearch("");
      }
    }
    previousSearchText.current = searchText;
  }, [searchText, touched, onCancelSearch, onSearch]);

  useEffect(() => {
    if (initialized.current === null) {
      initialized.current = true;
    }
  }, []);

  useEffect(() => {
    setSearchText(resolvedValue);
    if (!resolvedValue) {
      setSearchHelpText("");
    }
  }, [resolvedValue]);

  useEffect(
    () => () => {
      if (debounceTimeout.current) {
        clearTimeout(debounceTimeout.current);
      }
    },
    [],
  );

  return (
    <TextField
      size="small"
      name="search"
      autoComplete="off"
      variant="outlined"
      fullWidth
      disabled={Boolean(isSearching)}
      slotProps={{
        input: {
          endAdornment: isSearching ? (
            <CircularProgress />
          ) : (
            <InputAdornment position="end">
              <IconButton
                onClick={() => handleSearch()}
                color="primary"
                disabled={Boolean(!searchText || searchText.length < min)}
                size="large"
              >
                <SearchRounded />
              </IconButton>{" "}
            </InputAdornment>
          ),
        },
      }}
      helperText={searchHelpText}
      label={label || t("Buscar")}
      value={searchText}
      onChange={handleChange}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          handleSearch();
        }
      }}
    />
  );
};

TextInputSearch.propTypes = {
  onSearch: PropTypes.func.isRequired,
  min: PropTypes.number,
  cancelSearch: PropTypes.func,
  onCancelSearch: PropTypes.func,
  label: PropTypes.string,
  isSearching: PropTypes.bool,
  onChange: PropTypes.func,
  initialValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

TextInputSearch.defaultProps = {
  min: 3,
  isSearching: false,
  onSearch: () => {},
  onCancelSearch: undefined,
  cancelSearch: () => {},
  label: "Buscar",
  onChange: undefined,
  initialValue: "",
  value: undefined,
};

export default React.memo(TextInputSearch);
